import {
  Add,
  Button,
  Form,
  FormCard,
  FormItem,
  Trash,
  theme,
} from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { Form as AntdForm, Input } from 'antd'
import { useState } from 'react'
import styled from 'styled-components'
import { postJSON } from 'common/services/api'
import { useMutation } from 'react-query'
import { showToast } from 'common/utils'
import { useDispatch, useSelector } from 'react-redux'
import {
  HomeDto,
  RotaSettingsUpdateBody,
  ContractType,
  CommonErrors,
} from '@lmc/ts-common'
import * as GlobalActions from 'actions/Global'
import { useWatch } from 'antd/lib/form/Form'
import { handleSubmit } from 'screens/Rostering/formComponents/utils'
import { getIsDifferentRule, getRequiredRule } from 'screens/Rostering/utils'
import FixedFooter from './FixedFooter'

type ContractsFormType = {
  contractTypes: Array<ContractType>
}

type ContractsFormProps = {
  isDirty: boolean
  setIsDirty: (dirty: boolean) => void
}

const ContractsForm = ({ isDirty, setIsDirty }: ContractsFormProps) => {
  const { t } = useTranslation()
  const [form] = AntdForm.useForm()
  const dispatch = useDispatch()
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )
  useWatch('contractTypes', form)

  const initialValues = {
    contractTypes: home.rotaSettings?.contractTypes,
  }

  const mutationFunction = (
    contractTypes: RotaSettingsUpdateBody['contractTypes']
  ) =>
    postJSON({
      method: 'PUT',
      url: `/list/homes/${home.id}/rota`,
      json: { contractTypes } as RotaSettingsUpdateBody,
    })

  const { mutateAsync: updateContractsAndAbsence } = useMutation<
    { data: HomeDto },
    unknown,
    ContractsFormType,
    void
  >(
    ['rota-settings', home.id],
    (values: ContractsFormType) => mutationFunction(values.contractTypes),
    {
      onSuccess: (res) => {
        dispatch(GlobalActions.setHomeDetails(res.data))
        showToast({ title: t('ROTA_SETTINGS_UPDATED') })
      },
    }
  )

  const handleOnFinish = async (values: ContractsFormType) => {
    try {
      setIsUpdateLoading(true)
      setIsDirty(false)
      await updateContractsAndAbsence(values)
      setIsUpdateLoading(false)
    } catch (err: any) {
      const errorCode = err?.code

      if (
        errorCode === CommonErrors.Codes.rotaSettingsTypeInUse ||
        errorCode === CommonErrors.Codes.fundingTypeInUse
      ) {
        showToast({
          title: t(`YOU_CANNOT_DELETE_THE_ITEM_WHEN_IT_IS_IN_USE`),
          type: 'error',
        })

        const newContractValue: ContractsFormType['contractTypes'] =
          values.contractTypes.map((value) => ({
            ...value,
            isDeleted: false,
          }))

        form.setFieldValue('contractTypes', newContractValue)
      } else {
        showToast({
          title: err?.message || t('SETTINGS_ERROR'),
          type: 'error',
        })
      }
      setIsUpdateLoading(false)
    }
    setIsDirty(false)
  }

  const handleFormValuesChange = (values: ContractsFormType) => {
    if (values) {
      setIsDirty(true)
    }
  }

  const getDeleteFieldKey = (fieldName: number) => [
    'contractTypes',
    fieldName,
    'isDeleted',
  ]

  const deleteHandler = (fieldName: number) => {
    setIsDirty(true)
    const deleteFieldName = getDeleteFieldKey(fieldName)
    form.setFieldValue(deleteFieldName, true)
  }

  const filterDeletedFields = (fieldName: number) => {
    const deleteFieldName = getDeleteFieldKey(fieldName)
    const isDeleted = form.getFieldValue(deleteFieldName)

    return isDeleted !== true
  }

  const getIsContractDifferentRule = (fieldName: number) => {
    const formValues = form.getFieldsValue(true)
    const currentFormValues = formValues.contractTypes || []

    return getIsDifferentRule(currentFormValues, fieldName)
  }

  return (
    <Form
      form={form}
      onFinish={handleOnFinish}
      onValuesChange={handleFormValuesChange}
      initialValues={initialValues}
      validateTrigger={['']}
      data-testid="contracts-form"
    >
      <FormCard title={t('CONTRACT_TYPES')} data-testid="contracts-form-card">
        <AntdForm.List name="contractTypes">
          {(fields, { add }) => {
            const validFields = fields.filter((field) =>
              filterDeletedFields(field.name)
            )
            return (
              <OptionsContainer data-testid="contracts-form-options-container">
                {validFields.map((field) => (
                  <OptionContainer key={field.key} data-testid={`contracts-form-option-${field.name}`}>
                    <FormItem
                      name={[field.name, 'name']}
                      key={['contractTypes', field.key].join('-')}
                      rules={[
                        getRequiredRule(t('CONTRACT_TYPE')),
                        getIsContractDifferentRule(field.name),
                      ]}
                    >
                      <Input data-testid={`contracts-form-input-${field.name}`} />
                    </FormItem>
                    {validFields.length > 1 && (
                      <DeleteButton
                        variant="text"
                        onClick={() => deleteHandler(field.name)}
                        ariaLabel={t('DELETE_ITEM')}
                        data-testid={`contracts-form-delete-button-${field.name}`}
                      >
                        <Trash color={theme.color.neutral.color4} />
                      </DeleteButton>
                    )}
                  </OptionContainer>
                ))}
                <Button type="button" variant="text" onClick={() => add()} data-testid="contracts-form-add-button">
                  <Add />
                  {t('ADD_A_NEW_CONTRACT_TYPE')}
                </Button>
              </OptionsContainer>
            )
          }}
        </AntdForm.List>
      </FormCard>
      <FixedFooter
        isUpdateLoading={isUpdateLoading}
        isDirty={isDirty}
        handleSubmit={() => handleSubmit(form, handleOnFinish)}
      />
    </Form>
  )
}

const DeleteButton = styled(Button)`
  height: 24px;
`

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 440px;
`

export default ContractsForm
